import { useLingui } from '@lingui/react/macro';
import { formatNumber } from '@worldcoo/ui/lib/utils';
import { Image } from '@/routes/-components/image';
import { useTranslation } from '@/lib/i18n';
import { buildCampaignHref } from '../portal-href';
import type { PartnerNgoCampaign } from './use-partner-ngos';

function Reach(props: { value: number; label: string }) {
	const { language } = useTranslation();
	return (
		<div className="flex flex-col">
			<span className="text-2xl leading-[1.5] font-medium text-black tabular-nums">
				{formatNumber(language, props.value, 0)}
			</span>
			<span className="text-xs leading-normal text-[#7a7a7a]">{props.label}</span>
		</div>
	);
}

/**
 * Not a variant of the carousel tile, so each matches its own hand-off. A plain `<a>`: the merchant's
 * portal is another origin, and the backend drops merchants without one.
 */
export function NgoCampaignCard(props: { campaign: PartnerNgoCampaign; baseUrl: string }) {
	const { t } = useLingui();
	const { language } = useTranslation();
	const campaign = props.campaign;
	const raised = Math.round(campaign.collectedCents / 100);
	const goal = campaign.economicGoal;
	const pct = goal && goal > 0 ? Math.min(100, Math.round((raised / goal) * 100)) : undefined;
	const hasReach = campaign.beneficiaries.direct > 0 || campaign.beneficiaries.indirect > 0;

	return (
		<a
			href={buildCampaignHref(props.baseUrl, campaign.portalId, campaign.slug)}
			target="_blank"
			rel="noopener noreferrer"
			className="flex flex-col gap-4 rounded-3xl bg-white p-4 shadow-[0_9px_24px_0_rgba(46,20,0,0.12)] transition-shadow hover:shadow-[0_12px_32px_0_rgba(46,20,0,0.2)] sm:flex-row"
		>
			<div className="flex shrink-0 flex-col gap-4 sm:w-[292px]">
				<div className="relative h-[197px] overflow-hidden rounded-lg">
					<Image
						src={campaign.coverImage}
						alt={campaign.name}
						height={394}
						aspectRatio={292 / 197}
						objectFit="cover"
						className="absolute inset-0 size-full object-cover!"
					/>
					{/* So a pale photograph still separates from the white card. */}
					<div aria-hidden className="absolute inset-0 bg-gradient-to-br from-black/20 to-transparent" />
				</div>

				{/* Two zeroes mean the NGO never filled the field; "0 beneficiarios" would state a falsehood. */}
				{hasReach && (
					<div className="flex gap-10">
						<Reach value={campaign.beneficiaries.direct} label={t`Direct beneficiaries`} />
						<Reach value={campaign.beneficiaries.indirect} label={t`Indirect beneficiaries`} />
					</div>
				)}
			</div>

			<div className="flex min-w-0 flex-1 flex-col gap-4">
				<div className="flex flex-col gap-[5px]">
					<p className="text-xs leading-[1.3] font-medium tracking-[-0.02em]">
						<span className="text-ink">{formatNumber(language, raised, 0)} €</span>
						{goal ? (
							<span className="text-ink/50">
								{' '}
								{t`of`} {formatNumber(language, goal, 0)} €
							</span>
						) : null}
					</p>
					{pct !== undefined && (
						<div className="bg-sand h-[3px] overflow-hidden rounded">
							<div className="bg-brand h-full rounded-full" style={{ width: `${pct}%` }} />
						</div>
					)}
				</div>

				<h3 className="text-ink text-xl leading-[1.4] font-medium tracking-[-0.02em]">{campaign.name}</h3>

				{/* The server caps the text; the clamp keeps every card the same height. */}
				<p className="line-clamp-6 text-sm leading-[22px] text-black">{campaign.description}</p>
				<span className="sr-only">{t`(opens in a new tab)`}</span>
			</div>
		</a>
	);
}
